import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { 
  PieChart, TrendingUp, TrendingDown, Filter, Download, RefreshCw,
  ChevronDown, ChevronRight, Settings2, ChevronLeft, ChevronsLeft, ChevronsRight, Search, X, Store
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { StockAgingPanel } from '../components/StockAgingPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Performance category colors
const categoryColors = {
  'Very Low': 'bg-red-100 text-red-700 border-red-200',
  'Low': 'bg-orange-100 text-orange-700 border-orange-200',
  'Below Avg': 'bg-yellow-100 text-yellow-700 border-yellow-200',
  'Avg': 'bg-blue-100 text-blue-700 border-blue-200',
  'High': 'bg-green-100 text-green-700 border-green-200',
};

const categoryBgColors = {
  'Very Low': 'bg-red-50',
  'Low': 'bg-orange-50',
  'Below Avg': 'bg-yellow-50',
  'Avg': 'bg-blue-50',
  'High': 'bg-green-50',
};

// Friendly platform labels for the per-platform sales breakdown
const platformLabels = {
  amazon_uae: 'Amazon UAE',
  amazon_ksa: 'Amazon KSA',
  amazon_au: 'Amazon AU',
  noon_uae: 'Noon UAE',
  amazon_ind: 'Amazon India',
  flipkart: 'Flipkart',
  myntra: 'Myntra',
  offline: 'Offline Store',
  ajio: 'Ajio',
};

// Ordered list of marketplaces for the multi-select filter
const MARKETPLACES = Object.keys(platformLabels);

export default function SKUAnalysisPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [vendors, setVendors] = useState([]);
  const [currencyRates, setCurrencyRates] = useState({});
  const [showCurrencyDialog, setShowCurrencyDialog] = useState(false);
  const [editedRates, setEditedRates] = useState({});
  const [savingRates, setSavingRates] = useState(false);
  
  // Filters
  const [groupBy, setGroupBy] = useState('color_parent');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [sortBy, setSortBy] = useState('velocity');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [days, setDays] = useState(90);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]); // empty = all marketplaces
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState(''); // For debouncing
  
  const { toast } = useToast();
  
  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== searchQuery) {
        setSearchQuery(searchInput);
        setPage(1); // Reset to page 1 on new search
      }
    }, 400); // 400ms debounce
    
    return () => clearTimeout(timer);
  }, [searchInput, searchQuery]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        group_by: groupBy,
        sort_by: sortBy,
        sort_order: sortOrder,
        page: page.toString(),
        page_size: pageSize.toString(),
        days: days.toString(),
      });
      
      if (categoryFilter && categoryFilter !== 'all') {
        params.append('category_filter', categoryFilter);
      }
      if (vendorFilter && vendorFilter !== 'all') {
        params.append('vendor_filter', vendorFilter);
      }
      // Server-side search across all pages
      if (searchQuery && searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }
      // Marketplace filter (sum of selected; empty = all)
      if (selectedPlatforms.length > 0) {
        params.append('platforms', selectedPlatforms.join(','));
      }
      
      const response = await axios.get(`${API}/analytics/sku-analysis?${params}`);
      setData(response.data);
      setCurrencyRates(response.data.currency_rates || {});
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load SKU analysis',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [groupBy, categoryFilter, vendorFilter, sortBy, sortOrder, page, pageSize, searchQuery, days, selectedPlatforms, toast]);

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/analytics/sku-analysis/vendors`);
      setVendors(response.data.vendors || []);
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchVendors();
  }, [fetchData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({
        group_by: groupBy,
        sort_by: sortBy,
        sort_order: sortOrder,
        days: days.toString(),
      });
      
      if (categoryFilter && categoryFilter !== 'all') {
        params.append('category_filter', categoryFilter);
      }
      if (vendorFilter && vendorFilter !== 'all') {
        params.append('vendor_filter', vendorFilter);
      }
      if (selectedPlatforms.length > 0) {
        params.append('platforms', selectedPlatforms.join(','));
      }
      
      const response = await axios.get(`${API}/analytics/sku-analysis/export?${params}`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sku_analysis_${groupBy}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({ title: 'Success', description: 'Export downloaded successfully' });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to export data',
        variant: 'destructive',
      });
    }
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };

  const handleSaveCurrencyRates = async () => {
    setSavingRates(true);
    try {
      await axios.put(`${API}/analytics/currency-rates`, editedRates);
      setCurrencyRates(editedRates);
      setShowCurrencyDialog(false);
      toast({ title: 'Success', description: 'Currency rates updated' });
      fetchData(); // Refresh data with new rates
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update currency rates',
        variant: 'destructive',
      });
    } finally {
      setSavingRates(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(value);
  };

  const { groups = [], summary = {}, pagination = {} } = data || {};

  // Groups are now filtered server-side, just use directly
  const filteredGroups = groups;

  if (loading && !data) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <p className="text-muted-foreground">Loading SKU analysis...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight font-heading" data-testid="page-title">
              SKU Analysis
            </h1>
            <p className="text-muted-foreground mt-2">
              Track performance of products based on {days}-day sales velocity
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => { setEditedRates(currencyRates); setShowCurrencyDialog(true); }}
              data-testid="currency-settings-btn"
            >
              <Settings2 className="w-4 h-4 mr-2" />
              Currency Rates
            </Button>
            <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={handleExport} data-testid="export-btn">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Stock Aging & Dead Stock Dashboard */}
        <StockAgingPanel />

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="border border-border bg-card rounded-sm p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Total Groups</p>
            <p className="text-2xl font-bold font-mono" data-testid="total-groups">
              {summary.total_groups?.toLocaleString() || 0}
            </p>
          </div>
          <div className="border border-border bg-card rounded-sm p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Units Sold ({days}d)</p>
            <p className="text-2xl font-bold font-mono" data-testid="total-units">
              {summary.total_units_sold?.toLocaleString() || 0}
            </p>
          </div>
          <div className="border border-border bg-card rounded-sm p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Revenue (INR)</p>
            <p className="text-2xl font-bold font-mono" data-testid="total-revenue">
              {formatCurrency(summary.total_revenue_inr || 0)}
            </p>
          </div>
          <div className="border border-border bg-card rounded-sm p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">COGS (INR)</p>
            <p className="text-2xl font-bold font-mono" data-testid="total-cogs">
              {formatCurrency(summary.total_cogs || 0)}
            </p>
          </div>
          <div className="border border-border bg-card rounded-sm p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Avg Velocity</p>
            <p className="text-2xl font-bold font-mono" data-testid="avg-velocity">
              {summary.total_groups > 0 
                ? ((summary.total_units_sold || 0) / days / summary.total_groups).toFixed(2)
                : '0.00'}
            </p>
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="flex flex-wrap gap-3">
          {Object.entries(summary.category_breakdown || {}).map(([category, count]) => (
            <div 
              key={category} 
              className={`px-4 py-2 rounded-sm border ${categoryColors[category] || 'bg-gray-100'}`}
            >
              <span className="font-medium">{category}</span>
              <span className="ml-2 font-mono">{count}</span>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 p-4 border border-border bg-card rounded-sm">
          {/* Search */}
          <div className="flex items-center gap-2 flex-1 min-w-[200px] max-w-[300px]">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search SKU or Color Parent..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9 pr-8"
                data-testid="search-input"
              />
              {searchInput && (
                <button
                  onClick={() => { setSearchInput(''); setSearchQuery(''); }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {loading && searchInput && (
              <span className="text-xs text-muted-foreground">Searching...</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">Filters:</span>
          </div>
          
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">Period:</Label>
            <Select value={days.toString()} onValueChange={(v) => { setDays(parseInt(v)); setPage(1); }}>
              <SelectTrigger className="w-32" data-testid="days-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
                <SelectItem value="120">120 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">Markets:</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-48 justify-between font-normal" data-testid="markets-filter-btn">
                  <span className="flex items-center gap-2 truncate">
                    <Store className="w-4 h-4 text-muted-foreground" />
                    {selectedPlatforms.length === 0
                      ? 'All Markets'
                      : selectedPlatforms.length === 1
                        ? platformLabels[selectedPlatforms[0]]
                        : `${selectedPlatforms.length} markets`}
                  </span>
                  <ChevronDown className="w-4 h-4 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="start">
                <DropdownMenuLabel className="flex items-center justify-between">
                  Marketplaces
                  {selectedPlatforms.length > 0 && (
                    <button
                      className="text-xs text-blue-600 hover:underline"
                      onClick={() => { setSelectedPlatforms([]); setPage(1); }}
                      data-testid="markets-clear-btn"
                    >
                      Clear (All)
                    </button>
                  )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {MARKETPLACES.map((p) => (
                  <DropdownMenuCheckboxItem
                    key={p}
                    checked={selectedPlatforms.includes(p)}
                    onCheckedChange={(checked) => {
                      setSelectedPlatforms((prev) =>
                        checked ? [...prev, p] : prev.filter((x) => x !== p)
                      );
                      setPage(1);
                    }}
                    onSelect={(e) => e.preventDefault()}
                    data-testid={`market-option-${p}`}
                  >
                    {platformLabels[p]}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">Group By:</Label>
            <Select value={groupBy} onValueChange={(v) => { setGroupBy(v); setPage(1); }}>
              <SelectTrigger className="w-40" data-testid="group-by-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="color_parent">Color Parent</SelectItem>
                <SelectItem value="parent_sku">Parent SKU</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">Category:</Label>
            <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v); setPage(1); }}>
              <SelectTrigger className="w-36" data-testid="category-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="Very Low">Very Low</SelectItem>
                <SelectItem value="Low">Low</SelectItem>
                <SelectItem value="Below Avg">Below Avg</SelectItem>
                <SelectItem value="Avg">Avg</SelectItem>
                <SelectItem value="High">High</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">Vendor:</Label>
            <Select value={vendorFilter} onValueChange={(v) => { setVendorFilter(v); setPage(1); }}>
              <SelectTrigger className="w-40" data-testid="vendor-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Vendors</SelectItem>
                {vendors.map(vendor => (
                  <SelectItem key={vendor} value={vendor}>{vendor}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">Sort:</Label>
            <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setPage(1); }}>
              <SelectTrigger className="w-32" data-testid="sort-by-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="velocity">Velocity</SelectItem>
                <SelectItem value="units_sold">Units Sold</SelectItem>
                <SelectItem value="revenue">Revenue</SelectItem>
                <SelectItem value="alphabetical">Alphabetical</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
              data-testid="sort-order-btn"
            >
              {sortOrder === 'desc' ? <TrendingDown className="w-4 h-4" /> : <TrendingUp className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Data Table */}
        <div className="border border-border bg-card rounded-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left p-3 text-xs uppercase tracking-widest text-muted-foreground w-8"></th>
                  <th className="text-left p-3 text-xs uppercase tracking-widest text-muted-foreground">
                    {groupBy === 'color_parent' ? 'Color Parent' : 'Parent SKU'}
                  </th>
                  <th className="text-center p-3 text-xs uppercase tracking-widest text-muted-foreground">SKUs</th>
                  <th className="text-center p-3 text-xs uppercase tracking-widest text-muted-foreground">Performance</th>
                  <th className="text-right p-3 text-xs uppercase tracking-widest text-muted-foreground">Velocity</th>
                  <th className="text-right p-3 text-xs uppercase tracking-widest text-muted-foreground">Units Sold</th>
                  <th className="text-right p-3 text-xs uppercase tracking-widest text-muted-foreground">Revenue (INR)</th>
                  <th className="text-right p-3 text-xs uppercase tracking-widest text-muted-foreground">COGS (INR)</th>
                  <th className="text-left p-3 text-xs uppercase tracking-widest text-muted-foreground">Vendors</th>
                </tr>
              </thead>
              <tbody>
                {filteredGroups.map((group) => (
                  <React.Fragment key={group.group_key}>
                    {/* Group Row */}
                    <tr 
                      className={`border-b border-border hover:bg-muted/30 cursor-pointer ${categoryBgColors[group.performance_category] || ''}`}
                      onClick={() => toggleGroup(group.group_key)}
                      data-testid={`group-row-${group.group_key}`}
                    >
                      <td className="p-3">
                        {expandedGroups.has(group.group_key) 
                          ? <ChevronDown className="w-4 h-4" />
                          : <ChevronRight className="w-4 h-4" />
                        }
                      </td>
                      <td className="p-3 font-mono font-medium">{group.group_key}</td>
                      <td className="p-3 text-center font-mono">{group.sku_count}</td>
                      <td className="p-3 text-center">
                        <Badge className={categoryColors[group.performance_category]}>
                          {group.performance_category}
                        </Badge>
                      </td>
                      <td className="p-3 text-right font-mono font-medium">{group.sales_velocity.toFixed(2)}</td>
                      <td className="p-3 text-right font-mono">{group.total_units_sold.toLocaleString()}</td>
                      <td className="p-3 text-right font-mono">{formatCurrency(group.total_revenue_inr)}</td>
                      <td className="p-3 text-right font-mono">{formatCurrency(group.total_cogs)}</td>
                      <td className="p-3 text-sm text-muted-foreground">{group.vendors.join(', ')}</td>
                    </tr>

                    {/* Color-parent level marketplace split (always visible) */}
                    <tr className="border-b border-border bg-muted/10" data-testid={`group-platform-breakdown-${group.group_key}`}>
                      <td className="px-3 pb-2"></td>
                      <td className="px-3 pb-2" colSpan={8}>
                        {group.platform_breakdown && Object.keys(group.platform_breakdown).length > 0 ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs uppercase tracking-widest text-muted-foreground mr-1">
                              Markets ({days}d):
                            </span>
                            {Object.entries(group.platform_breakdown)
                              .sort((a, b) => b[1] - a[1])
                              .map(([plat, qty]) => (
                                <Badge key={plat} variant="secondary" className="font-mono text-xs">
                                  {platformLabels[plat] || plat}: {qty}
                                </Badge>
                              ))}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">
                            No sales in the selected period{selectedPlatforms.length > 0 ? ' / markets' : ''}
                          </span>
                        )}
                      </td>
                    </tr>
                    
                    {/* Expanded SKU Details */}
                    {expandedGroups.has(group.group_key) && group.skus.map((sku) => (
                      <React.Fragment key={sku.sku}>
                        <tr className="border-b border-border bg-muted/20">
                          <td className="p-2"></td>
                          <td className="p-2 pl-8 font-mono text-sm">{sku.sku}</td>
                          <td className="p-2 text-center text-sm text-muted-foreground" title={sku.name}>
                            {sku.name?.substring(0, 20)}...
                          </td>
                          <td className="p-2"></td>
                          <td className="p-2 text-right font-mono text-sm">{sku.velocity.toFixed(3)}</td>
                          <td className="p-2 text-right font-mono text-sm">{sku.quantity_sold.toLocaleString()}</td>
                          <td className="p-2 text-right font-mono text-sm">{formatCurrency(sku.revenue_inr)}</td>
                          <td className="p-2 text-right font-mono text-sm">{formatCurrency(sku.cogs)}</td>
                          <td className="p-2 text-sm text-muted-foreground">{sku.vendor || '-'}</td>
                        </tr>
                        {/* Per-platform units-sold breakdown (for Amazon Seller Central cross-checking) */}
                        <tr className="border-b border-border bg-muted/10" data-testid={`platform-breakdown-${sku.sku}`}>
                          <td className="p-2"></td>
                          <td className="p-2 pl-8" colSpan={8}>
                            {sku.platform_breakdown && Object.keys(sku.platform_breakdown).length > 0 ? (
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-xs uppercase tracking-widest text-muted-foreground mr-1">
                                  By platform ({days}d):
                                </span>
                                {Object.entries(sku.platform_breakdown)
                                  .sort((a, b) => b[1] - a[1])
                                  .map(([plat, qty]) => (
                                    <Badge key={plat} variant="outline" className="font-mono text-xs">
                                      {platformLabels[plat] || plat}: {qty}
                                    </Badge>
                                  ))}
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground italic">No sales on any platform in this period</span>
                            )}
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
                
                {filteredGroups.length === 0 && (
                  <tr>
                    <td colSpan={9} className="p-8 text-center text-muted-foreground">
                      {searchInput ? `No results found for "${searchInput}"` : 'No data found for the selected filters'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="p-4 border-t border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Page {pagination.page} of {pagination.total_pages} ({pagination.total_groups} groups)
                </span>
                <Select value={pageSize.toString()} onValueChange={(v) => { setPageSize(parseInt(v)); setPage(1); }}>
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(1)}
                  disabled={page <= 1}
                >
                  <ChevronsLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(pagination.total_pages, p + 1))}
                  disabled={page >= pagination.total_pages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(pagination.total_pages)}
                  disabled={page >= pagination.total_pages}
                >
                  <ChevronsRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Currency Settings Dialog */}
      <Dialog open={showCurrencyDialog} onOpenChange={setShowCurrencyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Currency Conversion Rates</DialogTitle>
            <DialogDescription>
              Set exchange rates for converting platform revenues to INR
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid gap-4">
              <div className="flex items-center justify-between">
                <Label>1 AED (UAE Dirham) =</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.1"
                    className="w-24"
                    value={editedRates.AED || ''}
                    onChange={(e) => setEditedRates(prev => ({ ...prev, AED: parseFloat(e.target.value) || 0 }))}
                    data-testid="rate-aed"
                  />
                  <span className="text-sm text-muted-foreground">INR</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Label>1 SAR (Saudi Riyal) =</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.1"
                    className="w-24"
                    value={editedRates.SAR || ''}
                    onChange={(e) => setEditedRates(prev => ({ ...prev, SAR: parseFloat(e.target.value) || 0 }))}
                    data-testid="rate-sar"
                  />
                  <span className="text-sm text-muted-foreground">INR</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Label>1 AUD (Australian Dollar) =</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.1"
                    className="w-24"
                    value={editedRates.AUD || ''}
                    onChange={(e) => setEditedRates(prev => ({ ...prev, AUD: parseFloat(e.target.value) || 0 }))}
                    data-testid="rate-aud"
                  />
                  <span className="text-sm text-muted-foreground">INR</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Label>1 USD (US Dollar) =</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    step="0.1"
                    className="w-24"
                    value={editedRates.USD || ''}
                    onChange={(e) => setEditedRates(prev => ({ ...prev, USD: parseFloat(e.target.value) || 0 }))}
                    data-testid="rate-usd"
                  />
                  <span className="text-sm text-muted-foreground">INR</span>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCurrencyDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCurrencyRates} disabled={savingRates}>
              {savingRates ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
